import axios from 'axios';

const csrfToken = document.querySelector('[name="csrf-token"]') || document.head.querySelector('meta[name="csrf-token"]') || { content: 'no-csrf-token' };

const transport = axios.create({
  withCredentials: true,
  credentials: 'include',
  headers: {
    common: {
      'X-CSRF-Token': csrfToken.content,
    },
  },
});

const url = window.location.href.includes('localhost') ? 'http://localhost:9112' : 'https://billing-api.superagi.com';
let BASE_PATH = process.env.NODE_ENV === 'development' ? url : 'https://billing-api.superagi.com';
if (process.env.NODE_ENV === 'staging') {
  BASE_PATH = 'https://billing-api-staging.superagi.com';
}

const salesUrl = 'http://localhost:3000';
let SALES_PATH = process.env.NODE_ENV === 'development' ? salesUrl : 'https://sales.superagi.com';
if (process.env.NODE_ENV === 'staging') {
  SALES_PATH = 'https://sales-staging.superagi.com';
}

let calculateDueCancelToken = null;

export default {
  getBaseSalesPath() {
    return SALES_PATH;
  },

  // Version endpoint
  async getVersion() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/v3/dashboard/version`);
      return response.data;
    } catch (error) {
      console.error('Error fetching version:', error);
      return null;
    }
  },

  // Get billing summary for the workspace
  async getBillingSummary() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/v3/dashboard/summary`);
      return response.data;
    } catch (error) {
      console.error('Error fetching billing summary:', error);
      return null;
    }
  },

  // Get all apps from the app store
  async getApps() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/v3/dashboard/apps`);
      return response.data;
    } catch (error) {
      console.error('Error fetching apps:', error);
      return null;
    }
  },

  // Get all app subscriptions for the workspace
  async getAppSubscriptions() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/v3/dashboard/subscriptions`);
      return response.data;
    } catch (error) {
      console.error('Error fetching app subscriptions:', error);
      return null;
    }
  },

  async updateBilling(period, packSize) {
    try {
      const response = await transport.post(`${BASE_PATH}/api/v3/dashboard/billing/update`, {
        period: period,
        pack_size: packSize,
      });
      return response.data;
    } catch (error) {
      console.error('Error updating billing:', error, error.response);
      return error.response.data;
    }
  },

  // Create a new app subscription
  async createAppSubscription(appId, planCode, billingPeriod, seatCount = 1) {
    try {
      const response = await transport.post(`${BASE_PATH}/api/v3/dashboard/subscriptions`, {
        app_id: appId,
        plan_code: planCode,
        billing_period: billingPeriod,
        seat_count: seatCount,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating app subscription:', error);
      return null;
    }
  },

  // Update an existing app subscription
  async updateAppSubscription(subscriptionId, planCode, billingPeriod, seatCount) {
    try {
      const response = await transport.put(`${BASE_PATH}/api/v3/dashboard/subscriptions/${subscriptionId}`, {
        plan_code: planCode,
        billing_period: billingPeriod,
        seat_count: seatCount,
      });
      return response.data;
    } catch (error) {
      console.error('Error updating app subscription:', error);
      return null;
    }
  },

  // Cancel an app subscription
  async cancelAppSubscription(subscriptionId) {
    try {
      const response = await transport.delete(`${BASE_PATH}/api/v3/dashboard/subscriptions/${subscriptionId}`);
      return response.data;
    } catch (error) {
      console.error('Error canceling app subscription:', error);
      return null;
    }
  },

  // Get or create billing account
  async getOrCreateBillingAccount() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/v3/dashboard/account`);
      return response.data;
    } catch (error) {
      console.error('Error getting or creating billing account:', error);
      return null;
    }
  },

  // Create trial checkout session
  async createTrialCheckoutSession(appId, planCode, billingPeriod = 'monthly', seatCount = 1, successUrl = '', cancelUrl = '', uiMode = 'embedded') {
    try {
      const response = await transport.post(`${BASE_PATH}/api/v3/dashboard/checkout/trial`, {
        app_id: appId,
        plan_code: planCode,
        billing_period: billingPeriod,
        seat_count: seatCount,
        success_url: successUrl,
        cancel_url: cancelUrl,
        ui_mode: uiMode,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating trial checkout session:', error);
      return null;
    }
  },

  // Create regular checkout session
  async createCheckoutSession(appId, planCode, billingPeriod = 'monthly', seatCount = 1, successUrl = '', cancelUrl = '', uiMode = 'embedded') {
    try {
      const response = await transport.post(`${BASE_PATH}/api/v3/dashboard/checkout`, {
        app_id: appId,
        plan_code: planCode,
        billing_period: billingPeriod,
        seat_count: seatCount,
        success_url: successUrl,
        cancel_url: cancelUrl,
        ui_mode: uiMode,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      return null;
    }
  },

  // Logout user
  async logout() {
    try {
      const response = await transport.post(`${BASE_PATH}/api/dashboard/user/me/logout`);
      localStorage.clear();
      return response.data;
    } catch (error) {
      console.error('Error logging out:', error);
      return null;
    }
  },

  // Check user authentication
  async checkUser(workspaceId = '') {
    try {
      const response = await transport.get(`${BASE_PATH}/api/dashboard/user/me?workspace_id=${workspaceId}`);
      return response.data;
    } catch (error) {
      localStorage.clear();
      window.location.href = `${SALES_PATH}/auth_users?next=${encodeURIComponent(window.location.href)}`;
      return null;
    }
  },

  // Get current workspace
  async getWorkspaceCurrent() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/dashboard/workspace/current`);
      return response.data;
    } catch (error) {
      console.error('Error fetching workspace current:', error);
      return null;
    }
  },

  // Switch workspace
  async switchWorkspace(workspaceId) {
    try {
      const response = await transport.post(`${BASE_PATH}/api/dashboard/workspace/change/${workspaceId}`, {});
      return response.data;
    } catch (error) {
      console.error('Error switching workspace:', error);
      return null;
    }
  },

  // Get user count
  async userCount() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/dashboard/workspace/usercount`, {});
      return response.data;
    } catch (error) {
      console.error('Error counting user:', error);
      return null;
    }
  },

  // Create setup intent for payment method collection
  async createSetupIntent() {
    try {
      const response = await transport.post(`${BASE_PATH}/api/v3/dashboard/billing/setup-intent`, {});
      return response.data;
    } catch (error) {
      console.error('Error creating setup intent:', error);
      return null;
    }
  },

  // Get credit packs
  async getCreditPacks() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/v3/dashboard/credit_packs`);
      return response.data;
    } catch (error) {
      console.error('Error fetching credit packs:', error);
      return null;
    }
  },

  async calculateDue(period, packSize) {
    if (calculateDueCancelToken) {
      calculateDueCancelToken.cancel('Request canceled due to new calculation.');
    }
    calculateDueCancelToken = axios.CancelToken.source();
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/v3/dashboard/billing/calculate_due`,
        {
          period: period,
          pack_size: packSize,
        },
        {
          cancelToken: calculateDueCancelToken.token,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error calculating due:', error);
      return null;
    } finally {
      calculateDueCancelToken = null;
    }
  },

  // Get enabled billing periods based on user location
  async getEnabledBillingPeriods() {
    try {
      const response = await transport.get(`${BASE_PATH}/api/v3/dashboard/billing/enabled_billing_periods`);
      return response.data;
    } catch (error) {
      console.error('Error fetching enabled billing periods:', error);
      return null;
    }
  },
};
